'use client';

import { useEffect, useState } from 'react';

import { useCommunityMessageContext } from '../contexts/community-message/useCommunityMessageContext';
import { useCommunityContext } from '../contexts/community/useCommunityContext';
import { subscribeToChannelMessageDoc } from '../functions/message/subscribeToChannelMessageDoc';
import type { ExtendedCommunityChannelMessage } from '../types/ExtendedCommunityChannelMessage';

/**
 * Live view of a single message — merges the message's own Firestore doc
 * listener over the seeded (search-API) message, so reaction counts, reply
 * counts, pin state, edits and deletions stay live on ANY rendered message,
 * including older ones the list-level subscription (anchored on the newest
 * seed's `createdAt`) never re-delivers. 1:1 port of PopshopRN's
 * `LiveCommunityChannelMessageViewImpl` (per-message doc tier).
 *
 * Returns `null` when the doc has been hard-deleted (caller hides the bubble).
 * Still-sending optimistic messages have no server doc yet, so the listener is
 * skipped and the optimistic seed is returned as-is.
 *
 * When the live doc reveals a pin-state flip (or the message going away), it
 * fires `pinnedMessagesInvalidated$` so the pinned banner + pane refetch — the
 * upstream `handleMessageChange` behaviour. This is the ONLY signal those views
 * get for pin edits on already-seeded (older) messages, which the anchored
 * `messages$` list subscription never re-delivers.
 */
export function useLiveMessage(
  seed: ExtendedCommunityChannelMessage,
  /**
   * When the underlying doc is gone (hard-deleted), keep the message visible as
   * a deleted placeholder built from the seed instead of returning `null`. Used
   * by the bookmarks panel — a bookmark keeps its own snapshot, so a deleted
   * message reads as "This message has been deleted." rather than vanishing
   * (upstream's `gone` behaviour).
   */
  keepWhenGone = false,
): ExtendedCommunityChannelMessage | null {
  const { creatorId } = useCommunityContext();
  const { pinnedMessagesInvalidated$ } = useCommunityMessageContext();
  const [live, setLive] = useState<
    ExtendedCommunityChannelMessage | null | undefined
  >(undefined);

  const { id: messageId, channelId } = seed;
  const skip = seed.isSending === true;
  const seedPinned = seed.pinnedAt != null;

  useEffect(() => {
    if (skip || !creatorId || !channelId || !messageId) {
      setLive(undefined);
      return;
    }
    // Baseline against the seed's pin state so a stale seed self-corrects on
    // the first live emission; tracked per-subscription so a recycled row
    // (new `messageId`) starts fresh.
    let prevPinned = seedPinned;
    return subscribeToChannelMessageDoc(
      creatorId,
      channelId,
      messageId,
      next => {
        const pinnedNow = next != null && next.pinnedAt != null;
        if (next === null || pinnedNow !== prevPinned) {
          prevPinned = pinnedNow;
          pinnedMessagesInvalidated$.next();
        }
        setLive(next);
      },
    );
  }, [
    creatorId,
    channelId,
    messageId,
    skip,
    seedPinned,
    pinnedMessagesInvalidated$,
  ]);

  if (live === null) {
    return keepWhenGone ? { ...seed, deleted: true } : null;
  }
  return live ? { ...seed, ...live } : seed;
}
